import { Clear } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import { GreenText, LabelField } from 'components'
import { getChargeTypes } from 'controllers/invoicing/index'
import { VehicleDocumentDto, vehicleDocumentValidationSchema, VehicleDto } from 'dtos'
import { ChargeTypeDto } from 'dtos/invoicing'
import { chargeInit } from 'dtos/invoicing/ChargeDto'
import { ChargeType } from 'enums/Invoicing'
import { Formik, getIn } from 'formik'
import { useEffect, useState } from 'react'
import { formatCurrency } from 'utils'

interface VehicleDocumentUploadDialogProps {
  initialValues: VehicleDocumentDto
  vehicle: VehicleDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: any) => void
  open: boolean
}

export default function VehicleDocumentUploadDialog({ initialValues, vehicle, isLoading, onClose, onSave, open }: VehicleDocumentUploadDialogProps) {
  const [chargeTypes, setChargeTypes] = useState<ChargeTypeDto[]>([])

  useEffect(() => {
    getChargeTypes().then(res => setChargeTypes(res.value))
  }, [])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={false}
        validateOnChange={true}
        validationSchema={vehicleDocumentValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} alignItems='center'>
                <DialogTitle>Document Upload</DialogTitle>

                {/* File Upload */}
                <Grid item xs={12}>
                  <input
                    accept='.pdf,.docx,.doc,.png,.jpeg,.jpg,.bmp'
                    hidden
                    type='file'
                    id='documentFileInput'
                    onChange={e => {
                      if (e.target.files && e.target.files.length > 0) {
                        setFieldValue('documentFile', e.target.files[0])
                      }
                    }}
                  />
                  <Button disabled={isLoading || isSubmitting} onClick={() => document.getElementById('documentFileInput')?.click()} variant='contained'>
                    UPLOAD FILE
                  </Button>
                  <Typography color='#808080' display='inline' variant='body2' sx={{ ml: 1 }}>
                    {values.documentFile?.name ?? values.displayFileName ?? 'No file uploaded'}
                  </Typography>
                  {errors.documentFile && (
                    <Typography color='error' variant='body2'>
                      {errors.documentFile}
                    </Typography>
                  )}
                </Grid>

                {/* Charge Section */}
                <Grid item xs={12}>
                  <FormLabel component='legend'>Does this file have an associated charge?</FormLabel>

                  <RadioGroup
                    onChange={e => {
                      if (e.target.value === 'true') setFieldValue('charge', chargeInit)
                      else setFieldValue('charge', null)
                    }}
                    row
                    value={values.charge ? 'true' : 'false'}
                  >
                    <FormControlLabel control={<Radio disabled={isLoading || isSubmitting} />} disabled={isLoading || isSubmitting} label='No' value='false' />
                    <FormControlLabel control={<Radio disabled={isLoading || isSubmitting} />} disabled={isLoading || isSubmitting} label='Yes' value='true' />
                  </RadioGroup>
                </Grid>

                {values.charge && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        disabled={isLoading || isSubmitting}
                        error={getIn(errors.charge, 'chargeTypeId')}
                        fullWidth
                        helperText={getIn(errors.charge, 'chargeTypeId')}
                        label='Charge Type'
                        name='chargeType'
                        onChange={e => setFieldValue('charge.chargeTypeId', e.target.value)}
                        select
                      >
                        {chargeTypes.map(ct => (
                          <MenuItem key={ct.chargeTypeId} value={ct.chargeTypeId}>
                            {ct.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        disabled={isLoading || isSubmitting}
                        error={getIn(touched.charge, 'description') && getIn(errors.charge, 'description')}
                        fullWidth
                        helperText={getIn(touched.charge, 'description') && getIn(errors.charge, 'description')}
                        inputProps={{ maxLength: 1000 }}
                        label='Description'
                        multiline
                        name='charge.description'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        rows={4}
                        value={values.charge?.description ?? ''}
                      />
                    </Grid>

                    {values.charge?.chargeTypeId === ChargeType.FedEx && (
                      <>
                        <Grid item xs={12}>
                          <Typography fontWeight='bold' variant='body1'>
                            Applicable VINs
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography color='primary' variant='body1'>
                            {vehicle.vin}
                          </Typography>
                        </Grid>

                        {values.charge?.additionalVINs?.map((vin, index) => (
                          <Grid item xs={12} container spacing={1} alignItems='center'>
                            <Grid item xs={true}>
                              <TextField
                                disabled={isLoading || isSubmitting}
                                error={Boolean(getIn(touched, `charge.additionalVINs[${index}]`) && getIn(errors, `charge.additionalVINs[${index}]`))}
                                fullWidth
                                helperText={getIn(touched, `charge.additionalVINs[${index}]`) && getIn(errors, `charge.additionalVINs[${index}]`)}
                                inputProps={{ maxLength: 17 }}
                                name={`charge.additionalVINs[${index}]`}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.charge?.additionalVINs![index] ?? ''}
                              />
                            </Grid>

                            <Grid item xs='auto'>
                              <IconButton
                                color='error'
                                disabled={isLoading || isSubmitting}
                                onClick={() =>
                                  setFieldValue(
                                    'charge.additionalVINs',
                                    values.charge?.additionalVINs?.filter((_, i) => i !== index)
                                  )
                                }
                              >
                                <Clear />
                              </IconButton>
                            </Grid>
                          </Grid>
                        ))}

                        <Grid item xs={12}>
                          <Button
                            color='primary'
                            disabled={isLoading || isSubmitting}
                            fullWidth
                            onClick={() => setFieldValue('charge.additionalVINs', [...(values.charge?.additionalVINs ?? []), ''])}
                            variant='contained'
                          >
                            ADD ADDITIONAL VIN
                          </Button>
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        disabled={isLoading || isSubmitting}
                        error={getIn(touched.charge, 'amount') && getIn(errors.charge, 'amount')}
                        fullWidth
                        helperText={getIn(touched.charge, 'amount') && getIn(errors.charge, 'amount')}
                        InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                        inputProps={{ step: '0.01' /*, min: '0'*/ }}
                        label='Amount'
                        name='charge.amount'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type='number'
                        value={values.charge?.amount ?? ''}
                      />
                    </Grid>

                    {values.charge?.chargeTypeId === ChargeType.FedEx && (
                      <>
                        <Grid item xs={12}>
                          <Button
                            color='primary'
                            disabled={isLoading || isSubmitting}
                            fullWidth
                            onClick={() => {
                              const amount = values.charge?.amount ?? 0
                              // Add 1 to account for the principal vehicle's VIN, which is not in additionalVINs.
                              const vins = (values.charge?.additionalVINs?.length ?? 0) + 1
                              const amountPerVIN = amount / vins

                              setFieldValue('charge.amountPerVIN', amountPerVIN)
                            }}
                            variant='contained'
                          >
                            CALCULATE AMOUNT PER VIN
                          </Button>
                        </Grid>

                        <Grid item xs={12}>
                          <LabelField
                            error={getIn(errors, 'charge.amountPerVIN')}
                            label='Amount Per VIN'
                            value={formatCurrency(values.charge?.amountPerVIN ?? 0)}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  <GreenText variant='outlined'>
                    {`**Upload File:**
                  
Any uploaded files will not be OCR'd, parsed, or machine read and are simply for keeping vehicle-related documents organized. Allowed file types are .pdf, .docx, .doc, .png, .jpeg, .jpg, and .bmp with a maximum allowed file size of 20 MB. 

When users upload documents to the vehicle, any uploaded documents will have a “File Type” of “Miscellaneous” under Vehicle Details – Documents (tab). 

**Does this file have an associated charge:** 

By default, “Does this file type have an associated charge” is set to “No”. If “Does this file type have an associated charge” is set to “Yes”, these fields become visible and required to save: 

   - Charge Type (dropdown) 
   - Description (Textbox, 1000-character limit) 
   - Amount (Currency field, supports values in 0, 0.0 or 0.00 format) 

**Charge Type:** 

The “Charge Type” dropdown lists these options: 

   - FedEx
   - Replacement Label
   - Repairs
   - Vehicle Clean Up
   - Miscellaneous

If “Does this file type have an associated charge” is set to “Yes”, and the selected “Charge Type” is set to “FedEx”, these fields are displayed below the “Charge Type” dropdown and are all required: 

   - Description (Textbox, 1000-character limit) 
   - Applicable VINs (label displaying the VIN users are currently under along with “Add Additional VIN” button which adds an “Enter VIN” input row. 17-char max.) 
   - Amount (Currency field, supports values in 0, 0.0 or 0.00 format) 
   - “Calculate Amount Per VIN” button 
   - Amount per VIN (Currency field, supports values in 0, 0.0 or 0.00 format) 

**Applicable VINs:**

The Applicable VINs section displays the VIN that the user is under as a label followed by the “Add Additional VIN” button and pressing this button adds an input row above this button. All fields are required to save.

**Amount:**

The Amount field is for entering the total dollar amount printed on the FedEx receipt. 

**Calculate Amount Per VIN:** 

When users press “Calculate Amount Per VIN”, the prorated amount per VIN (Amount field value divided by count of VINs) is determined and displayed in the “Amount Per VIN” label. If users press “Calculate Amount Per VIN” and the “Amount” field is empty or contains a value that is less than $0.01, the “Amount” field’s border will turn red to indicate it is in error and the “Amount Per VIN” will not be calculated. 

**SAVE:** 

If “Does this file type have an associated charge” is set to “Yes”, an entry is added to the Transactions table under Vehicle Details – Invoicing (tab). 

If “Does this file type have an associated charge” is set to “Yes” AND the selected “Charge Type” = “FedEx”, when a user presses “SAVE”, the following checks occur: 

   - If an “Additional VIN” field contains less than 17 characters, VTS2 will not save, the input field’s border will turn red to indicate it is in error, and an error message will be displayed saying, “VIN field must contain 17 characters to save”. 
   - If an “Additional VIN” field contains 17 characters but the VIN does not exist in VTS2, VTS2 will not save, the input field’s border will turn red to indicate it is in error, and an error message will be displayed saying, “(VIN) is not in VTS2. Please remove VIN and try again.” 
   - If an “Additional VIN” field contains 17 characters but the VIN has been Canceled in VTS2, VTS2 will not save, the input field’s border will turn red to indicate it is in error, and an error message will be displayed saying, “(VIN) is Canceled. Please remove VIN and try again.” 
   - If all “Additional VIN” fields contain 17 characters a VIN has been entered more than once, VTS2 will not save and an error message will be displayed saying, “Duplicate VINs entered. Please remove duplicates and try again.” 

If all above checks pass when saving: 

   - The uploaded FedEx receipt is added as a Document to all applicable VINS. 
   - A line item is added to each vehicle’s Vehicle Details – Invoicing tab’s Transactions table. 
   - The “FedEx” Invoicing Services line item on each VIN the FedEx receipt was uploaded to has the prorated FedEx receipt amount (Amount Per VIN) added. 

If users delete an uploaded document where “Does this file type have an associated charge” is set to “Yes”, a negative entry is added to the vehicle’s transactions table, deducting the Amount associated with the uploaded document. This same amount is then deducted from the selected “Charge Type” on the “Invoicing” card under Vehicle Details – Invoicing (tab).`}
                  </GreenText>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={isLoading || isSubmitting} variant='text' color='inherit' onClick={onClose}>
                CANCEL
              </Button>

              <LoadingButton loading={isLoading || isSubmitting} type='submit' onClick={() => console.log(errors)} variant='contained'>
                SAVE
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  )
}
